import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Mail, Link2, Paperclip, AlertTriangle, Trash2, Reply } from "lucide-react";

/**
 * INBOX simulation. Used by phishing / social-engineering / safe-content
 * triage concepts. Events emitted: message_opened, sender_inspected,
 * link_hovered, link_clicked:<id>, attachment_opened:<id>,
 * message_reported, complied_with_request:<id>, sensitive_info_shared,
 * credentials_entered, final_decision (detail = report:<id>).
 */
export default function InboxSim({ scenario, recorder, onFinalize, busy }) {
  const messages = scenario.messages || [];
  const [openId, setOpenId] = useState(messages[0]?.id || null);
  const [reportedIds, setReportedIds] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [opened, setOpened] = useState(new Set());
  const [hovered, setHovered] = useState(new Set());
  const [showCredModal, setShowCredModal] = useState(false);

  const open = useMemo(() => messages.find((m) => m.id === openId), [messages, openId]);

  const onSelect = (m) => {
    setOpenId(m.id);
    if (!opened.has(m.id)) {
      recorder.log("message_opened", m.id);
      setOpened((p) => new Set([...p, m.id]));
    }
  };

  const inspectSender = () => recorder.log("sender_inspected", open.id);
  const hoverLink = () => {
    recorder.log("link_hovered", open.id);
    setHovered((p) => new Set([...p, open.id]));
  };
  const clickLink = () => recorder.log("link_clicked:" + open.id, open.linkUrl);
  const openAttach = () => recorder.log("attachment_opened:" + open.id, open.attachmentName);
  const replyComply = () => recorder.log("complied_with_request:" + open.id, "replied");
  const reportMsg = () => {
    if (!reportedIds.includes(open.id)) setReportedIds([...reportedIds, open.id]);
    recorder.log("message_reported", open.id);
  };
  const deleteMsg = () => {
    if (!deletedIds.includes(open.id)) setDeletedIds([...deletedIds, open.id]);
    recorder.log("message_deleted", open.id);
  };

  const finalize = () => {
    const decision = reportedIds.length > 0 ? "report:" + reportedIds.join(",") : "no_action";
    onFinalize(decision, { reportedIds, deletedIds });
  };

  return (
    <div className="grid lg:grid-cols-12 min-h-[480px]" data-testid="inbox-sim">
      <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-[var(--iddl-rule)]">
        <div className="px-4 py-2 label-mono border-b border-[var(--iddl-rule)] surface flex items-center gap-2">
          <Mail size={12} /> Inbox · {messages.length}
        </div>
        <ul>
          {messages.map((m) => (
            <li key={m.id}>
              <button
                onClick={() => onSelect(m)}
                data-testid={`inbox-msg-${m.id}`}
                className={`w-full text-left p-3 border-b border-[var(--iddl-rule)] hover:bg-[var(--iddl-surface)] ${openId === m.id ? "bg-[var(--iddl-surface)]" : ""} ${deletedIds.includes(m.id) ? "opacity-40 line-through" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-display font-semibold text-sm truncate">{m.from}</span>
                  {reportedIds.includes(m.id) && <span className="label-mono tint-cs px-1.5 py-0.5 border ml-auto">REPORTED</span>}
                </div>
                <div className="text-sm mt-0.5 truncate">{m.subject}</div>
                <div className="flex items-center gap-2 mt-1 text-[11px] text-[var(--iddl-muted)]">
                  {m.hasLink && <Link2 size={10} />}
                  {m.hasAttachment && <Paperclip size={10} />}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="lg:col-span-8 p-5">
        {!open && <div className="label-mono">Select a message</div>}
        {open && (
          <div data-testid={`inbox-open-${open.id}`}>
            <div className="flex items-start gap-3 flex-wrap">
              <div>
                <div className="label-mono">From</div>
                <div className="font-mono-disp text-sm" data-testid="inbox-sender">{open.from}</div>
              </div>
              <Button size="sm" variant="outline" className="rounded-none" onClick={inspectSender} data-testid="btn-inspect-sender">
                Inspect sender
              </Button>
            </div>

            <h3 className="font-display text-xl font-semibold mt-4">{open.subject}</h3>
            <p className="text-sm mt-3 leading-relaxed text-[var(--iddl-ink)]">{open.body}</p>

            {open.hasLink && (
              <div className="mt-4 paper border p-3 flex flex-wrap items-center gap-3" data-testid="inbox-link-card">
                <Link2 size={14} />
                <span className="text-sm">Link in this message</span>
                <span
                  className="font-mono-disp text-xs text-[var(--iddl-muted)] border-b border-dashed border-[var(--iddl-rule)] cursor-help"
                  onMouseEnter={hoverLink}
                  data-testid="inbox-link-hover"
                >
                  {hovered.has(open.id) ? open.linkUrl : "Hover to reveal real URL"}
                </span>
                <Button size="sm" variant="outline" className="rounded-none ml-auto" onClick={clickLink} data-testid="btn-click-link">
                  Click link
                </Button>
              </div>
            )}

            {open.hasAttachment && (
              <div className="mt-3 paper border p-3 flex items-center gap-3" data-testid="inbox-attachment-card">
                <Paperclip size={14} />
                <span className="font-mono-disp text-xs" data-testid="inbox-attachment-name">{open.attachmentName}</span>
                <Button size="sm" variant="outline" className="rounded-none ml-auto" onClick={openAttach} data-testid="btn-open-attachment">
                  Open attachment
                </Button>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-none" onClick={() => setShowCredModal(true)} data-testid="btn-reply-credentials">
                <Reply size={12} className="mr-1" /> Reply with credentials
              </Button>
              <Button size="sm" variant="outline" className="rounded-none" onClick={replyComply} data-testid="btn-comply">
                Comply with request
              </Button>
              <Button size="sm" variant="outline" className="rounded-none" onClick={deleteMsg} data-testid="btn-delete">
                <Trash2 size={12} className="mr-1" /> Delete
              </Button>
              <Button size="sm" className="btn-ink rounded-none" onClick={reportMsg} data-testid="btn-report">
                <AlertTriangle size={12} className="mr-1" /> Report as suspicious
              </Button>
            </div>
          </div>
        )}

        <div className="mt-8 pt-4 border-t border-[var(--iddl-rule)] flex justify-end">
          <Button onClick={finalize} disabled={busy} className="btn-ink rounded-none" data-testid="btn-finalize-inbox">
            Finalise — submit triage
          </Button>
        </div>
      </div>

      {showCredModal && (
        <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" data-testid="cred-modal">
          <div className="paper p-6 max-w-md w-full">
            <div className="label-mono">Confirm action</div>
            <h3 className="font-display text-lg font-semibold mt-1">Reply with credentials</h3>
            <p className="text-sm text-[var(--iddl-muted)] mt-2">This action will be logged as <code>credentials_entered</code>.</p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" className="rounded-none" onClick={() => setShowCredModal(false)} data-testid="btn-cancel-creds">
                Cancel
              </Button>
              <Button className="btn-ink rounded-none" onClick={() => {
                recorder.log("credentials_entered", open?.id);
                recorder.log("complied_with_request:" + open?.id, "credentials");
                setShowCredModal(false);
              }} data-testid="btn-confirm-creds">
                Send credentials
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
